import { useTranslation } from 'react-i18next';
import AnalysisResultCard from 'components/AnalysisResultCard/AnalysisResultCard';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import {
  useCopyValueChainMapAnalysisResultMutation,
  useDeleteValueChainMapAnalysisResultByIdMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';

import useAiChatAnalysisResult from '../hooks/useAiChatAnalysisResult';
import useOpenImpactAnalysis from '../hooks/useOpenImpactAnalysis';

import type { ReactElement } from 'react';
import type IAiChatAnalysisResult from '../interfaces/IAiChatAnalysisResult';

import styles from '../sass/AiChat.module.scss';

/**
 * A run of an impact analysis, drawn in the reply with the card the map's panel draws it with.
 *
 * The same card, the same state: in progress it shows the panel's loader and follows the run
 * through the socket event and the poll; done, it shows the figures. Its actions work from here
 * as they do there — the eye opens the panel and shows the run on the map, re-run copies the
 * run's scenarios into the analysis and opens the panel on them, delete removes the run — and
 * every one of them moves the panel through the cache it shares with this card. The details
 * are the one thing the card does not unfold here: the scenarios and their units are the
 * panel's to show, so "View details" opens the panel on the run.
 */
function AiChatAnalysisResult({
  valueChainMapId, analysisId, name, resultId,
}: IAiChatAnalysisResult): ReactElement | null {
  const { t } = useTranslation();
  const {
    result, isLoading, isDeleted, isError,
  } = useAiChatAnalysisResult({
    valueChainMapId, analysisId, name, resultId,
  });
  const openImpactAnalysis = useOpenImpactAnalysis();
  const [copyResult, { isLoading: isCopying }] = useCopyValueChainMapAnalysisResultMutation();
  const [deleteResult, { isLoading: isDeleting }] = useDeleteValueChainMapAnalysisResultByIdMutation();

  const request = { valueChainMapId: `${valueChainMapId}`, analysisId, id: resultId };

  const handleView = () => {
    openImpactAnalysis({
      valueChainMapId, analysisId, name, resultId, view: true,
    });
  };

  const handleOpenDetails = () => {
    openImpactAnalysis({
      valueChainMapId, analysisId, name, resultId,
    });
  };

  // The copy lands in the analysis setup, so the panel opens on the analysis for the user to see it.
  const handleDuplicate = async () => {
    await copyResult(request).unwrap();
    openImpactAnalysis({ valueChainMapId, analysisId, name });
  };

  const handleDelete = async () => {
    await deleteResult(request).unwrap();
  };

  if (isDeleted) {
    return <p className={styles.note}>{t('aiChat.tools.impactResultDeleted')}</p>;
  }

  if (isError) {
    return <p className={styles.note}>{t('aiChat.tools.impactResultLoadFailed')}</p>;
  }

  if (isLoading || !result) {
    return (
      <div className={styles.toolLoader} role="status" aria-live="polite">
        <SmallLoader size={24} />
      </div>
    );
  }

  return (
    <AnalysisResultCard
      result={result}
      onOpenDetails={handleOpenDetails}
      onToggleVisibility={handleView}
      onDuplicate={handleDuplicate}
      onDelete={handleDelete}
      isDuplicateLoading={isCopying}
      isDeleteLoading={isDeleting}
      showHeaderActions={false}
    />
  );
}

export default AiChatAnalysisResult;
